import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import PublicationTypes from "constants/publication-types";
import PublicationRecord from "models/view-models/publication-record";
import BreadcrumbList from "molecules/lists/breadcrumb-list";
import { TableOfContentsItem } from "pages/notebooks/notebook-reference-panel/bookview/use-virtual-table-of-contents";
import {
    NotebookReferencePanelTabs,
    NotebookReferencePanelTabsNameMap,
} from "pages/notebooks/notebook-reference-panel/notebook-reference-panel";
import React, { useMemo } from "react";
import { CurrentPublicationAtomContext } from "utilities/contexts/use-current-publication";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import { t } from "utilities/localization-utils";
import useToggleNotebookReferencePanel from "./use-toggle-notebook-reference-panel";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import ModalCloseButton from "atoms/buttons/modal-close-button";

const BASE_CLASS_NAME = "c-notebook-reference-panel";

enum NotebookReferencePanelBreadcrumbs {
    ReferencePanel = 1,
    TableOfContents = 2,
    Bookview = 3,
}

interface NotebookReferencePanelHeaderProps {
    activeTab: NotebookReferencePanelTabs;
    showBookview: boolean;
    currentPublication?: PublicationRecord;
    currentTocItem?: TableOfContentsItem;
    setCurrentTocItem: (tocItem?: TableOfContentsItem) => void;
    setCurrentPublication: (
        currentPublication: CurrentPublicationAtomContext
    ) => void;
}

const NotebookReferencePanelHeader = (
    props: NotebookReferencePanelHeaderProps
) => {
    const {
        activeTab,
        currentPublication,
        showBookview,
        currentTocItem,
        setCurrentTocItem,
        setCurrentPublication,
    } = props;
    const { toggleDisplay, closePanel } = useToggleNotebookReferencePanel();
    const { isMobile } = useNotebookBreakpoints();
    const breadcrumbs = useMemo(() => {
        const list = [
            {
                name: t(NotebookReferencePanelTabsNameMap[activeTab]),
                data: NotebookReferencePanelBreadcrumbs.ReferencePanel,
            },
            {
                name: currentPublication?.getDisplayCodeAndEdition()!,
                data: NotebookReferencePanelBreadcrumbs.TableOfContents,
            },
        ];

        if (currentTocItem) {
            let name = "";

            switch (currentTocItem.publicationType) {
                case PublicationComponentType.Annex:
                    name = `${t("annex")} ${currentTocItem.nfpaLabel}`;
                    break;
                case PublicationComponentType.AnnexGroup:
                    name = `${t("annex")} ${currentTocItem.nfpaLabel}`;
                    break;
                case PublicationComponentType.Article:
                    name = `${t("article")} ${currentTocItem.nfpaLabel}`;
                    break;
                case PublicationComponentType.Chapter:
                    name = `${t("chapter")} ${currentTocItem.nfpaLabel}`;
                    break;
                default:
                    name =
                        currentPublication?.type === PublicationTypes.NEC &&
                        currentTocItem?.nfpaLabel !== "9"
                            ? `${t("article")} ${currentTocItem.nfpaLabel}`
                            : `${t("chapter")} ${currentTocItem.nfpaLabel}`;
            }

            list.push({
                name: name,
                data: NotebookReferencePanelBreadcrumbs.Bookview,
            });
        }

        return list;
    }, [activeTab, currentPublication, currentTocItem]);
    const handleBreadcrumbClick = (
        breadcrumb: NotebookReferencePanelBreadcrumbs
    ) => {
        switch (breadcrumb) {
            case NotebookReferencePanelBreadcrumbs.TableOfContents:
                setCurrentTocItem(undefined);
                break;
            case NotebookReferencePanelBreadcrumbs.ReferencePanel:
                setCurrentTocItem(undefined);
                setCurrentPublication({
                    publication: new PublicationRecord(),
                    loaded: false,
                    errors: [],
                });
                break;
            default:
                break;
        }
    };
    const GrabBar = () => (
        <div className={`${BASE_CLASS_NAME}__grabBarContainer`}>
            <div className={`${BASE_CLASS_NAME}__grabBar`} />
        </div>
    );
    return (
        <div className={`${BASE_CLASS_NAME}__heading`}>
            {isMobile && <GrabBar />}

            <div className={`${BASE_CLASS_NAME}__title`}>
                {currentTocItem || showBookview ? (
                    <BreadcrumbList
                        breadcrumbs={breadcrumbs}
                        onBreadcrumbClick={handleBreadcrumbClick}
                    />
                ) : (
                    <span>{t("reference-panel")}</span>
                )}
                {isMobile ? (
                    <Button
                        accessibleText={t("close")}
                        cssClassName="-modal-close -icon"
                        onClick={toggleDisplay}
                        size={ButtonSizes.Medium}
                        style={ButtonStyles.TertiaryAlt}>
                        <Icon type={Icons.Close} size={IconSizes.Large} />
                    </Button>
                ) : (
                    <ModalCloseButton onClick={closePanel} />
                )}
            </div>
        </div>
    );
};

export default NotebookReferencePanelHeader;
